import { useEffect, useRef, useState } from 'react';

import { usePreviousValue } from '../hooks';
import { getChangedSources, getInitialState } from './helpers';

const imageCache = {};

const useImageState = ({ decode, image }) => {
  const { initialState, sourceList } = getInitialState({ image, imageCache });
  const [state, setState] = useState(initialState);
  const [shouldAttemptLoad, setShouldAttemptLoad] = useState(false);

  const imageRef = useRef(null);
  const sources = useRef(sourceList);
  const previousSources = usePreviousValue(sources.current);

  useEffect(() => {
    if (state.isLoading) {
      loadImage();
    }
    return () => {
      if (imageRef.current) {
        unloadImage();
      }
    };
  }, [image]);

  useEffect(() => {
    if (previousSources && previousSources.length > 0) {
      const changedSources = getChangedSources({ image, previousSources });
      if (changedSources) {
        sources.current = changedSources;
        if (!changedSources.length) {
          return setState((prev) => ({
            ...prev,
            isLoaded: false,
            isLoading: false,
          }));
        }
        setState({ currentIndex: 0, isLoaded: false, isLoading: true });
        setShouldAttemptLoad(true);
      }
    }
  }, [image]);

  useEffect(() => {
    if (shouldAttemptLoad) {
      loadImage();
    }
  }, [shouldAttemptLoad]);

  const loadImage = () => {
    imageRef.current = new Image();
    imageRef.current.src = sources.current[state.currentIndex];
    if (decode && imageRef.current.decode) {
      imageRef.current.decode().then(onLoad).catch(onError);
    } else {
      imageRef.current.onerror = onError;
      imageRef.current.onload = onLoad;
    }
  };

  const unloadImage = () => {
    imageRef.current = null;
  };

  const onError = () => {
    imageCache[sources.current[state.currentIndex]] = false;
    if (!imageRef.current) {
      return false;
    }

    let nextIndex;
    for (
      nextIndex = state.currentIndex + 1;
      nextIndex < sources.current.length;
      nextIndex++
    ) {
      const src = sources.current[nextIndex];
      if (!imageCache[src] && imageCache[src] !== false) {
        setState((prev) => ({ ...prev, currentIndex: nextIndex }));
        break;
      }

      if (imageCache[src] === true) {
        setState({ currentIndex: nextIndex, isLoaded: true, isLoading: false });
        return true;
      }

      if (imageCache[src] === false) {
        continue;
      }
    }

    if (nextIndex === sources.current.length) {
      return setState((prev) => ({ ...prev, isLoading: false }));
    }

    loadImage();
  };

  const onLoad = () => {
    imageCache[sources.current[state.currentIndex]] = true;
    if (imageRef.current) {
      setState((prev) => ({ ...prev, isLoaded: true }));
    }
  };

  const getImageProps = () => ({ src: sources.current[state.currentIndex] });

  return { getImageProps, state };
};

export default useImageState;
